import { useState } from 'react';

import {
  Check,
  ChevronDown,
  ChevronUp,
  Loader2,
  Mail,
  Plus,
  TestTube2,
  Trash2,
  X,
} from 'lucide-react';

import { FormField, FormInput, FormSelect } from '@/components/form';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import {
  useEmailConnectors,
  useCreateEmailConnector,
  useUpdateEmailConnector,
  useDeleteEmailConnector,
  useTestEmailConnector,
} from '@/hooks/useEmailConnectors';
import { extractErrorMessage } from '@/lib/utils';

import type { EmailConnector, EmailConnectorProviderType } from '@repo/shared-types';

const PROVIDER_LABELS: Record<EmailConnectorProviderType, string> = {
  azure: 'Azure (Microsoft Graph)',
  resend: 'Resend',
  sendgrid: 'SendGrid',
  nodemailer: 'Nodemailer (SMTP)',
};

const CREDENTIAL_MASK = '••••••••';

interface CredentialFields {
  key: string;
  label: string;
  secret?: boolean;
  placeholder?: string;
}

const CREDENTIAL_FIELDS: Record<EmailConnectorProviderType, CredentialFields[]> = {
  azure: [
    { key: 'tenant_id', label: 'Tenant ID', placeholder: 'xxxxxxxx-xxxx-...' },
    { key: 'client_id', label: 'Client ID', placeholder: 'xxxxxxxx-xxxx-...' },
    { key: 'client_secret', label: 'Client Secret', secret: true },
    { key: 'webhook_secret', label: 'Webhook Secret', secret: true, placeholder: 'Optional' },
  ],
  resend: [{ key: 'api_key', label: 'API Key', secret: true }],
  sendgrid: [{ key: 'api_key', label: 'API Key', secret: true }],
  nodemailer: [
    { key: 'host', label: 'SMTP Host', placeholder: 'smtp.example.com' },
    { key: 'port', label: 'Port', placeholder: '587' },
    { key: 'user', label: 'Username' },
    { key: 'pass', label: 'Password', secret: true },
  ],
};

interface ConnectorFormData {
  name: string;
  provider_type: EmailConnectorProviderType;
  credentials: Record<string, string>;
  email_addresses: string[];
}

const emptyForm = (): ConnectorFormData => ({
  name: '',
  provider_type: 'azure',
  credentials: {},
  email_addresses: [''],
});

export const EmailConnectorsPanel = () => {
  const { toast } = useToast();
  const { data: connectors = [], isLoading } = useEmailConnectors();
  const createMutation = useCreateEmailConnector();
  const updateMutation = useUpdateEmailConnector();
  const deleteMutation = useDeleteEmailConnector();
  const testMutation = useTestEmailConnector();

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ConnectorFormData>(emptyForm());
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const resetForm = () => {
    setForm(emptyForm());
    setShowForm(false);
    setEditingId(null);
  };

  const startEdit = (connector: EmailConnector) => {
    setEditingId(connector.id);
    setForm({
      name: connector.name,
      provider_type: connector.provider_type,
      credentials: connector.credentials as Record<string, string>,
      email_addresses: connector.email_addresses.length > 0 ? [...connector.email_addresses] : [''],
    });
    setShowForm(true);
    setExpandedId(null);
  };

  const handleSave = async () => {
    const cleanAddresses = form.email_addresses.filter((e) => e.trim());
    if (!form.name.trim()) {
      toast({ variant: 'destructive', title: 'Name is required' });
      return;
    }
    if (cleanAddresses.length === 0) {
      toast({ variant: 'destructive', title: 'At least one email address is required' });
      return;
    }

    const cleanCredentials: Record<string, string> = {};
    for (const [key, value] of Object.entries(form.credentials)) {
      if (value && value !== CREDENTIAL_MASK) {
        cleanCredentials[key] = value;
      }
    }

    try {
      if (editingId) {
        await updateMutation.mutateAsync({
          id: editingId,
          dto: {
            name: form.name,
            credentials: cleanCredentials,
            email_addresses: cleanAddresses,
          },
        });
        toast({ variant: 'success', title: 'Connector updated' });
      } else {
        await createMutation.mutateAsync({
          name: form.name,
          provider_type: form.provider_type,
          credentials: cleanCredentials,
          email_addresses: cleanAddresses,
        });
        toast({ variant: 'success', title: 'Connector created' });
      }
      resetForm();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: editingId ? 'Failed to update connector' : 'Failed to create connector',
        description: extractErrorMessage(error),
      });
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Delete connector "${name}"? This cannot be undone.`)) return;
    try {
      await deleteMutation.mutateAsync(id);
      toast({ variant: 'success', title: `Connector "${name}" deleted` });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to delete connector',
        description: extractErrorMessage(error),
      });
    }
  };

  const handleTest = async (id: string) => {
    try {
      const result: { success: boolean; error?: string } = await testMutation.mutateAsync(id);
      toast({
        variant: result.success ? 'success' : 'destructive',
        title: result.success ? 'Connection successful' : 'Connection failed',
        description: result.error,
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Test failed',
        description: extractErrorMessage(error),
      });
    }
  };

  const updateCredential = (key: string, value: string) => {
    setForm((prev) => ({
      ...prev,
      credentials: { ...prev.credentials, [key]: value },
    }));
  };

  const updateEmailAddress = (index: number, value: string) => {
    setForm((prev) => {
      const updated = [...prev.email_addresses];
      updated[index] = value;
      return { ...prev, email_addresses: updated };
    });
  };

  const addEmailAddress = () => {
    setForm((prev) => ({
      ...prev,
      email_addresses: [...prev.email_addresses, ''],
    }));
  };

  const removeEmailAddress = (index: number) => {
    setForm((prev) => ({
      ...prev,
      email_addresses: prev.email_addresses.filter((_, i) => i !== index),
    }));
  };

  if (isLoading) return <p className="text-muted-foreground text-sm">Loading connectors...</p>;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Email Connectors</CardTitle>
            <CardDescription>
              Manage email provider connections. Each connector can have multiple email addresses.
            </CardDescription>
          </div>
          {!showForm && (
            <Button
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Connector
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {showForm && (
          <div className="space-y-4 rounded-lg border p-4">
            <h3 className="font-medium">{editingId ? 'Edit Connector' : 'New Connector'}</h3>

            <FormField label="Name" htmlFor="connector-name">
              <FormInput
                id="connector-name"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Marketing Resend, Support Azure"
              />
            </FormField>

            {!editingId && (
              <FormField label="Provider" htmlFor="connector-provider">
                <FormSelect
                  value={form.provider_type}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      provider_type: e.target.value as EmailConnectorProviderType,
                      credentials: {},
                    }))
                  }
                >
                  {Object.entries(PROVIDER_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </FormSelect>
              </FormField>
            )}

            <div className="space-y-2">
              <p className="text-muted-foreground text-xs font-medium">Credentials</p>
              {CREDENTIAL_FIELDS[form.provider_type].map((field) => (
                <FormField key={field.key} label={field.label} htmlFor={`cred-${field.key}`}>
                  <FormInput
                    id={`cred-${field.key}`}
                    type={field.secret ? 'password' : 'text'}
                    value={form.credentials[field.key] ?? ''}
                    onChange={(e) => updateCredential(field.key, e.target.value)}
                    placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                    autoComplete="off"
                  />
                </FormField>
              ))}
            </div>

            <div className="space-y-2">
              <p className="text-muted-foreground text-xs font-medium">Email Addresses</p>
              {form.email_addresses.map((email, i) => (
                <div key={i} className="flex gap-2">
                  <FormInput
                    value={email}
                    onChange={(e) => updateEmailAddress(i, e.target.value)}
                    placeholder="email@example.com"
                    className="flex-1"
                  />
                  {form.email_addresses.length > 1 && (
                    <Button variant="ghost" size="icon" onClick={() => removeEmailAddress(i)}>
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addEmailAddress}>
                <Plus className="mr-1 h-3 w-3" />
                Add Email
              </Button>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleSave}
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {(createMutation.isPending || updateMutation.isPending) && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {editingId ? 'Save Changes' : 'Create Connector'}
              </Button>
              <Button variant="outline" onClick={resetForm}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {connectors.length === 0 && !showForm && (
          <div className="text-muted-foreground py-8 text-center text-sm">
            No email connectors configured. Add one to start sending and receiving emails.
          </div>
        )}

        {connectors.map((connector: EmailConnector) => (
          <div key={connector.id} className="rounded-lg border">
            <div
              className="flex cursor-pointer items-center justify-between p-4"
              onClick={() => setExpandedId(expandedId === connector.id ? null : connector.id)}
            >
              <div className="flex items-center gap-3">
                <Mail className="text-muted-foreground h-5 w-5" />
                <div>
                  <p className="font-medium">{connector.name}</p>
                  <p className="text-muted-foreground text-xs">
                    {PROVIDER_LABELS[connector.provider_type] ?? connector.provider_type} &middot;{' '}
                    {connector.email_addresses.length} email(s)
                    {!connector.is_active && (
                      <span className="ml-2 text-yellow-600">(inactive)</span>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {connector.is_active ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
                    <Check className="h-3 w-3" />
                    Active
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
                    Inactive
                  </span>
                )}
                {expandedId === connector.id ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </div>
            </div>

            {expandedId === connector.id && (
              <div className="border-t px-4 py-3">
                <div className="mb-3 space-y-1">
                  <p className="text-muted-foreground text-xs font-medium">Email Addresses</p>
                  {connector.email_addresses.map((email: string) => (
                    <p key={email} className="text-sm">
                      {email}
                    </p>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => startEdit(connector)}>
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleTest(connector.id)}
                    disabled={testMutation.isPending}
                  >
                    {testMutation.isPending ? (
                      <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                    ) : (
                      <TestTube2 className="mr-1 h-3 w-3" />
                    )}
                    Test
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDelete(connector.id, connector.name)}
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="mr-1 h-3 w-3" />
                    Delete
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
